import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isBefore,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  Locale,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import { de, enUS, es, fr, ja } from "date-fns/locale";
import { Button } from "flowbite-react";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getPresets } from "../../shared/constants";
import { IDateRangePreset } from "../../interfaces";
// Custom hook for window width
const useWindowWidth = () => {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 0
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
};

// Map of supported locales
const localeMap: { [key: string]: Locale } = {
  en: enUS,
  es: es,
  de: de,
  fr: fr,
  ja: ja,
};

interface DateRangePickerProps {
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  onTempDateChange?: (startDate: Date, endDate: Date | null) => void;
  onClose?: () => void;
  initialStartDate?: Date;
  initialEndDate?: Date | null;
  initialPreset?: string;
  onPresetChange?: (preset: IDateRangePreset | undefined) => void;
  allowTodaySelection?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  onDateRangeChange,
  onTempDateChange,
  onClose,
  initialStartDate,
  initialEndDate,
  initialPreset,
  onPresetChange,
  allowTodaySelection = false,
}) => {
  const { t, i18n } = useTranslation();
  const currentLocale = localeMap[i18n.language] || localeMap.en;
  const today = startOfDay(new Date());
  const yesterday = subDays(today, 1);
  const presets = getPresets(t);
  const [currentMonth, setCurrentMonth] = useState(
    initialStartDate || yesterday
  );
  const [startDate, setStartDate] = useState<Date | null>(
    initialStartDate || null
  );
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate || null);
  const [selectedPreset, setSelectedPreset] = useState(initialPreset || "");
  const [isCustomRange, setIsCustomRange] = useState(false);
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 768;

  const formatWithLocale = (date: Date, formatStr: string) => {
    return format(date, formatStr, { locale: currentLocale });
  };

  const getLocalizedWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = addDays(startOfWeek(new Date()), i);
      days.push(formatWithLocale(date, "EEE"));
    }
    return days;
  };

  const isDateDisabled = (date: Date) => {
    const startOfDate = startOfDay(date);
    if (allowTodaySelection) {
      return isBefore(today, startOfDate); // Disable only future dates
    }
    return !isBefore(startOfDate, today); // Disable today and future dates
  };

  // Validate initial dates
  useEffect(() => {
    if (initialStartDate && isDateDisabled(initialStartDate)) {
      setStartDate(null);
    }
    if (initialEndDate && isDateDisabled(initialEndDate)) {
      setEndDate(null);
    }
    if (initialPreset && selectedPreset !== initialPreset) {
      handlePresetSelect(initialPreset);
    }
  }, []);

  const validateDateRange = (start: Date, end: Date): boolean => {
    // Check if either date is disabled
    if (isDateDisabled(start) || isDateDisabled(end)) {
      return false;
    }
    // Ensure start date is before or equal to end date
    return !isBefore(end, start);
  };

  const handlePresetSelect = (presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (preset) {
      const [newStartDate, newEndDate] = preset.getRange();

      // Validate the date range
      if (!validateDateRange(newStartDate, newEndDate)) {
        return;
      }

      setStartDate(newStartDate);
      setEndDate(newEndDate);
      setSelectedPreset(presetId);
      setCurrentMonth(newStartDate);
      onTempDateChange?.(newStartDate, newEndDate);
    }
  };

  const handleDateSelect = (date: Date) => {
    if (isDateDisabled(date)) return;

    if (!startDate || (startDate && endDate)) {
      setStartDate(date);
      setEndDate(null);
      setSelectedPreset("");
      onTempDateChange?.(date, null);
    } else {
      let newStartDate = date;
      let newEndDate = startDate;

      // Ensure correct order of dates
      if (isBefore(date, startDate)) {
        newStartDate = date;
        newEndDate = startDate;
      } else {
        newStartDate = startDate;
        newEndDate = date;
      }

      // Validate the date range
      if (!validateDateRange(newStartDate, newEndDate)) {
        return;
      }

      setStartDate(newStartDate);
      setEndDate(newEndDate);
      onTempDateChange?.(newStartDate, newEndDate);
      onPresetChange?.(undefined);
    }
  };

  const handleApply = () => {
    if (startDate && endDate && validateDateRange(startDate, endDate)) {
      onDateRangeChange?.(startDate, endDate);
      const preset = presets.find((p) => p.id === selectedPreset);
      onPresetChange?.(preset);
      onClose?.();
    }
  };

  const renderCalendar = (monthOffset: number = 0) => {
    const month = addMonths(currentMonth, monthOffset);
    const days = eachDayOfInterval({
      start: startOfMonth(month),
      end: endOfMonth(month),
    });

    const weekDays = getLocalizedWeekDays();

    const isInRange = (date: Date) => {
      if (startDate && endDate) {
        return isWithinInterval(date, { start: startDate, end: endDate });
      }
      return false;
    };

    // Calculate leading and trailing empty cells to always render 6 rows (42 cells)
    const leadingEmpty = startOfMonth(month).getDay();
    const totalCells = 42; // 6 rows * 7 columns
    const trailingEmpty = Math.max(totalCells - (leadingEmpty + days.length), 0);

    // Build dates from previous and next months to fill the grid (instead of blanks)
    const prevMonthLastDay = subDays(startOfMonth(month), 1);
    const nextMonthFirstDay = addDays(endOfMonth(month), 1);

    const leadingDates = Array.from({ length: leadingEmpty }).map((_, i) =>
      subDays(prevMonthLastDay, leadingEmpty - 1 - i)
    );
    const trailingDates = Array.from({ length: trailingEmpty }).map((_, i) =>
      addDays(nextMonthFirstDay, i)
    );

    const renderDayButton = (date: Date) => {
      const outOfMonth = !isSameMonth(date, month);
      // Out-of-month dates should be disabled regardless of actual date
      const isDisabled = isDateDisabled(date) || outOfMonth;
      const isSelected = startDate && endDate && isInRange(date) && !outOfMonth;
      const isRangeStart = startDate && isSameDay(date, startDate) && !outOfMonth;
      const isRangeEnd = endDate && isSameDay(date, endDate) && !outOfMonth;

      return (
        <button
          key={date.toISOString()}
          onClick={() => handleDateSelect(date)}
          disabled={isDisabled}
          className={`
            w-9 h-9 text-sm rounded-md flex items-center justify-center
            ${outOfMonth ? "text-gray-400" : ""}
            ${isDisabled ? "text-gray-300 cursor-not-allowed" : ""}
            ${isSelected && !isDisabled ? "bg-indigo-50" : ""}
            ${!isDisabled && !isSelected ? "hover:bg-gray-100" : ""}
            ${(isRangeStart || isRangeEnd) && !isDisabled
              ? "bg-indigo-500 text-white hover:bg-indigo-600"
              : ""
            }
          `}
        >
          {format(date, "d")}
        </button>
      );
    };

    return (
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold">
            {formatWithLocale(month, "MMMM yyyy")}
          </h2>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day) => (
            <div
              key={day}
              className="text-xs text-gray-500 font-medium text-center py-1"
            >
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {leadingDates.map((date) => renderDayButton(date))}
          {days.map((date) => renderDayButton(date))}
          {trailingDates.map((date) => renderDayButton(date))}
        </div>
      </div>
    );
  };

  const renderMobileView = () => {
    return (
      <div className="w-full">
        <div
          className={`grid grid-cols-2 gap-2 ${isCustomRange ? "mb-4" : ""}`}
        >
          {presets.map((preset) => (
            <Button
              key={preset.id}
              color={selectedPreset === preset.id ? "primary" : "light"}
              size="sm"
              onClick={() => {
                setIsCustomRange(false);
                handlePresetSelect(preset.id);
              }}
              className="w-full text-sm"
            >
              {preset.label}
            </Button>
          ))}
          <Button
            color={isCustomRange ? "primary" : "light"}
            size="sm"
            onClick={() => setIsCustomRange(true)}
            className="w-full text-sm col-span-1"
          >
            {t("common.custom")}
          </Button>
        </div>

        {isCustomRange && (
          <div className="mobile-calendar">
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <CaretLeft size={20} />
              </button>
              <h2 className="text-sm font-semibold">
                {formatWithLocale(currentMonth, "MMMM yyyy")}
              </h2>
              <button
                onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <CaretRight size={20} />
              </button>
            </div>
            {renderCalendar(0)}
          </div>
        )}
      </div>
    );
  };

  const renderDesktopView = () => {
    return (
      <div className="desktop-view flex bg-white rounded-lg shadow-lg">
        {/* Presets */}
        <div className="w-40 p-2 border-r">
          {presets.map((preset) => (
            <button
              key={preset.id}
              onClick={() => handlePresetSelect(preset.id)}
              className={`w-full text-left px-3 py-2 text-sm rounded-md ${selectedPreset === preset.id
                ? "bg-indigo-50 text-indigo-600"
                : "hover:bg-gray-100"
                }`}
            >
              {preset.label}
            </button>
          ))}
        </div>

        {/* Calendar */}
        <div className="flex-1">
          <div className="flex items-center justify-between p-4 border-b">
            <button
              onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <CaretLeft size={20} />
            </button>
            <div className="flex gap-4">
              {renderCalendar(0)}
              {renderCalendar(1)}
            </div>
            <button
              onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
              className="p-1 hover:bg-gray-100 rounded-full"
            >
              <CaretRight size={20} />
            </button>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t">
            <div className="text-sm">
              {startDate ? (
                <>
                  {formatWithLocale(startDate, "PP")}
                  {endDate ? ` - ${formatWithLocale(endDate, "PP")}` : " - "}
                </>
              ) : (
                t("common.selectDateRange")
              )}
            </div>
            <div className="flex gap-2">
              <Button color="light" size="sm" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button
                color="primary"
                size="sm"
                onClick={handleApply}
                disabled={!startDate || !endDate}
              >
                {t("common.apply")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return isMobile ? renderMobileView() : renderDesktopView();
};

export default DateRangePicker;
